import { clientMatchTerms } from './client-match-terms.js';
import { normalizedText } from './text.js';
/**
 * Matches a calendar/meeting title to a client id — a port of Timesheet's
 * `findClientIdFromEventTitle` (`platform/timesheet/src/lib/calendar/helpers.ts`),
 * generalized so both Timesheet and meeting enrichment share one implementation.
 *
 * Scoring: for every client, the best-matching alias (from {@link clientMatchTerms}) wins.
 * An alias from the client's own name or prefix beats any repo-derived one (a repo word such
 * as "affinity" names a tool several clients use); within the same kind, longer, more
 * specific aliases beat shorter ones.
 * External clients always beat the L3-internal client when both match — a title
 * mentioning both "Loop3" and an external client is about the external client, not an
 * internal sync. `repos` (default `[]`) feeds `clientMatchTerms`'s repo-ownership
 * aliases; omit it when the caller has no `.ops/sync.yml` index available.
 *
 * Returns the matched client id, or `null` when nothing scores above zero (including
 * an empty/blank title).
 */
export function matchClientByTitle(title, clients, repos = []) {
    const normalizedTitle = normalizedText(title);
    if (!normalizedTitle)
        return null;
    let bestExternalMatch;
    let bestInternalMatch;
    for (const client of clients) {
        const score = bestAliasScore(normalizedTitle, aliasesForClient(client, repos));
        if (score <= 0)
            continue;
        if (isInternalClient(client)) {
            if (score > (bestInternalMatch?.score ?? 0))
                bestInternalMatch = { clientId: client.id, score };
        }
        else if (score > (bestExternalMatch?.score ?? 0)) {
            bestExternalMatch = { clientId: client.id, score };
        }
    }
    return bestExternalMatch?.clientId ?? bestInternalMatch?.clientId ?? null;
}
/**
 * Like {@link matchClientByTitle}, but suppresses a match that resolves to the
 * Loop3-internal client ({@link isInternalClient}) when the meeting had at least one
 * external attendee (`opts.hasExternalAttendee`) — a title mentioning "Loop3" (e.g. "Loop3
 * Check-In") is not proof of an internal meeting when someone outside Loop3 was actually on
 * the call; an external attendee always contradicts an internal classification. A match
 * resolving to an EXTERNAL client is never suppressed — this guard only ever narrows the
 * internal-match case, which is the exact live-bug class (PL-200) this exists to close.
 *
 * `hasExternalAttendee` is passed in already-computed rather than derived here from a raw
 * attendee list, so this package stays free of any email-domain-parsing concern (each
 * caller already has its own attendee shape and internal-domain knowledge — see
 * `@loop3/meeting-hub`'s `hasExternalAttendee`).
 */
export function matchClientByTitleGuarded(title, clients, repos = [], opts) {
    const clientId = matchClientByTitle(title, clients, repos);
    if (clientId === null || !opts.hasExternalAttendee)
        return clientId;
    const matchedClient = clients.find((client) => client.id === clientId);
    if (matchedClient && isInternalClient(matchedClient))
        return null;
    return clientId;
}
/**
 * Whether `client` is Loop3's own internal client (matched by name or `prefix: 'L3'`) —
 * the one client a title match should never resolve to for a meeting that also has an
 * external attendee (see {@link matchClientByTitleGuarded}).
 */
export function isInternalClient(client) {
    const name = normalizedText(client.name);
    const prefix = normalizedText(client.prefix ?? '');
    return name === 'loop3' || name === 'loop 3' || prefix === 'l3';
}
function aliasesForClient(client, repos) {
    const identity = new Set(clientMatchTerms(client).map(normalizedText));
    return clientMatchTerms(client, repos)
        .map(normalizedText)
        .filter(Boolean)
        .map((text) => ({ text, identity: identity.has(text) }));
}
/** A name/prefix alias beats any repo-derived one; within the same kind, longer aliases win. */
function bestAliasScore(title, aliases) {
    let score = 0;
    for (const alias of aliases) {
        if (containsAlias(title, alias.text)) {
            score = Math.max(score, compactText(alias.text).length + (alias.identity ? IDENTITY_BONUS : 0));
        }
    }
    return score;
}
/** Larger than any alias length, so a name/prefix match always outranks a repo-derived one. */
const IDENTITY_BONUS = 1000;
/** Compact (space-insensitive) matching needs this many letters; shorter aliases only match as whole words. */
const MIN_COMPACT_ALIAS = 5;
function containsAlias(title, alias) {
    if (!alias)
        return false;
    if (` ${title} `.includes(` ${alias} `))
        return true;
    const compactAlias = compactText(alias);
    // A short alias inside another word is a false hit ("ext" in "next", "mcp" in "scmcp").
    if (compactAlias.length < MIN_COMPACT_ALIAS)
        return false;
    return compactText(title).includes(compactAlias);
}
function compactText(value) {
    return normalizedText(value).replace(/\s+/g, '');
}
